import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { useAddTranslation } from "./use-documents";
import { useLocales } from "../locales/use-locales";
import { TOS_TRANSLATION_BODY_MAX, TOS_TRANSLATION_TITLE_MAX } from "../shared/compliance-types";
import { resolveComplianceError } from "../shared/resolve-compliance-error";

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly tosId: string;
  readonly existingLocales: readonly string[];
}

export function AddTranslationDialog({ open, onOpenChange, tosId, existingLocales }: Props) {
  const { t } = useTranslation();
  const localesQuery = useLocales();
  const add = useAddTranslation();

  // Filter out locales already on the doc — backend would 409
  // TOS_TRANSLATION_EXISTS otherwise, but the operator should never
  // see the failing path.
  const availableLocales = useMemo(
    () => (localesQuery.data ?? []).filter((l) => !existingLocales.includes(l.code)),
    [localesQuery.data, existingLocales],
  );

  const [localeCode, setLocaleCode] = useState<string>("");
  const [title, setTitle] = useState("");
  const [bodyMarkdown, setBodyMarkdown] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLocaleCode(availableLocales[0]?.code ?? "");
      setTitle("");
      setBodyMarkdown("");
      setError(null);
    }
  }, [open, availableLocales]);

  const trimmedTitle = title.trim();
  const trimmedBody = bodyMarkdown.trim();
  const isValid =
    localeCode.length > 0 &&
    trimmedTitle.length > 0 &&
    trimmedTitle.length <= TOS_TRANSLATION_TITLE_MAX &&
    trimmedBody.length > 0 &&
    trimmedBody.length <= TOS_TRANSLATION_BODY_MAX;

  const handleSubmit = async () => {
    if (!isValid || add.isPending) return;
    setError(null);
    try {
      await add.mutateAsync({
        tosId,
        input: {
          localeCode,
          title: trimmedTitle,
          bodyMarkdown: trimmedBody,
        },
      });
      onOpenChange(false);
    } catch (err) {
      setError(resolveComplianceError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !add.isPending && onOpenChange(next)}>
      <DialogContent data-testid="compliance-add-translation-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("compliance.documents.translations.addTitle")}</DialogTitle>
          <DialogDescription>
            {t("compliance.documents.translations.addDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {availableLocales.length === 0 && (
            <p
              data-testid="compliance-add-translation-no-locales"
              className="rounded-md border border-border bg-muted/30 p-2 text-sm text-muted-foreground"
            >
              {t("compliance.documents.translations.noAvailableLocales")}
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="tr-locale">{t("compliance.documents.translations.locale")}</Label>
            <select
              id="tr-locale"
              data-testid="compliance-add-translation-locale-input"
              value={localeCode}
              onChange={(e) => setLocaleCode(e.target.value)}
              disabled={availableLocales.length === 0}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {availableLocales.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.code} — {l.displayName}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tr-title">{t("compliance.documents.translations.titleField")}</Label>
            <Input
              id="tr-title"
              data-testid="compliance-add-translation-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={TOS_TRANSLATION_TITLE_MAX}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tr-body">{t("compliance.documents.translations.bodyField")}</Label>
            <Textarea
              id="tr-body"
              data-testid="compliance-add-translation-body-input"
              value={bodyMarkdown}
              onChange={(e) => setBodyMarkdown(e.target.value)}
              maxLength={TOS_TRANSLATION_BODY_MAX}
              rows={10}
              className="font-mono text-sm"
            />
          </div>

          {error && (
            <p data-testid="compliance-add-translation-error" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={add.isPending}>
            {t("compliance.actions.cancel")}
          </Button>
          <Button
            data-testid="compliance-add-translation-submit"
            onClick={handleSubmit}
            disabled={!isValid || add.isPending}
          >
            {add.isPending
              ? t("compliance.actions.saving")
              : t("compliance.documents.translations.add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
